import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import ApiHttp from "byzantine/src/ApiHttp";
import Transaction from "byzantine/src/Transaction";
import type { TransactionResponse } from "byzantine/src/Transaction";
import type Account from "byzantine/src/Account";
import { useNotificationContext } from "cerulean";
import { useAccountsContext } from "../contexts/AccountContext";
import SectionCard from "../SectionCard";
import RecentTransactionsTable, {
  EmptyTransactionState,
} from "../RecentTransactionsTable";
import usePendingTransactions from "../hooks/usePendingTransactions";

interface RecentTransactionsCardProps {
  replyTime: string;
}

const RecentTransactionsCard = ({ replyTime }: RecentTransactionsCardProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { accounts }: { accounts: Account[] } = useAccountsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>(
    []
  );
  const [settledTransactions, setSettledTransactions] = useState<Transaction[]>(
    []
  );
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>(
    []
  );
  usePendingTransactions({
    accounts,
    setPendingTransactions,
    excludeHiddenAccounts: true,
  });

  const fetchRecentTransactions = async () => {
    try {
      setIsLoading(true);
      const response = await ApiHttp.fetch(
        "transactions?filter=hidden&q=is:settled",
        { method: "GET" },
        { page: 1, per_page: 5 }
      );
      if (response?.transactions) {
        const transactions = response.transactions.map(
          (t: TransactionResponse) =>
            Transaction.deserialize(
              t,
              accounts.find((a) => a.id === t.account_id)
            )
        );
        setSettledTransactions(transactions);
      }
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("recent-transactions-error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentTransactions();
  }, [JSON.stringify(accounts)]);

  useEffect(() => {
    // pending transactions get returned in addition to the 5 posted transactions,
    // so we need to reduce it down to 5 transactions
    const transactions = Transaction.sortByPostedDate([
      ...pendingTransactions,
      ...settledTransactions,
    ]).slice(0, 5);
    setRecentTransactions(transactions);
  }, [
    JSON.stringify(pendingTransactions),
    JSON.stringify(settledTransactions),
  ]);

  return (
    <SectionCard isLoading={isLoading} kind="transactions">
      <SectionCard.Title text={l10n.getString("recent-transactions-header")} />
      {recentTransactions.length > 0 ? (
        <RecentTransactionsTable
          transactions={recentTransactions}
          replyTime={replyTime}
        />
      ) : (
        <EmptyTransactionState />
      )}
    </SectionCard>
  );
};
export default RecentTransactionsCard;
